/**
 * Event Bus
 *
 * Purpose: Loose coupling between services and components
 *
 * Use cases:
 *   - Notify UI when inventory changes (without direct imports)
 *   - Trigger cache invalidation across services
 *   - Broadcast offline/online status changes
 *   - Coordinate between POS, inventory, and sync services
 *
 * Pattern: Publish-Subscribe (PubSub)
 *
 * Benefits:
 *   - Decouples services (no circular dependencies)
 *   - Easy to test (mock event bus)
 *   - Flexible (add new listeners without changing emitters)
 *
 * Reference: LeltárMester Egyesített Refaktorálási Terv (v2.0)
 */

import { logger } from '@/services/logger';

// ============================================================================
// Types
// ============================================================================

/**
 * Event types (registry)
 *
 * Add new event types here as they are needed.
 */
export const EVENT_TYPES = {
  // Inventory events
  INVENTORY_UPDATED: 'inventory:updated',
  INVENTORY_DELETED: 'inventory:deleted',
  INVENTORY_SYNCED: 'inventory:synced',

  // Offline sync events
  SYNC_STARTED: 'sync:started',
  SYNC_COMPLETED: 'sync:completed',
  SYNC_FAILED: 'sync:failed',
  SYNC_PROGRESS: 'sync:progress',

  // Network status events
  ONLINE: 'network:online',
  OFFLINE: 'network:offline',

  // Cache events
  CACHE_INVALIDATED: 'cache:invalidated',
  CACHE_CLEARED: 'cache:cleared',

  // Auth events
  USER_LOGGED_IN: 'auth:logged_in',
  USER_LOGGED_OUT: 'auth:logged_out',

  // Add more as needed...
} as const;

/**
 * Event type (for type safety)
 */
export type EventType = (typeof EVENT_TYPES)[keyof typeof EVENT_TYPES];

/**
 * Event payload (generic)
 */
export type EventPayload = unknown;

/**
 * Event listener callback
 */
export type EventListener<T = EventPayload> = (payload: T) => void | Promise<void>;

/**
 * Event subscription
 */
interface EventSubscription {
  id: string; // Unique subscription ID
  type: EventType; // Event type
  listener: EventListener; // Callback function
  once: boolean; // Remove after first execution
  subscribedAt: number; // Timestamp
}

// ============================================================================
// Event Bus Class
// ============================================================================

class EventBus {
  private subscriptions = new Map<EventType, EventSubscription[]>();
  private subscriptionCounter = 0;

  /**
   * Subscribe to an event
   *
   * @param type Event type
   * @param listener Callback function
   * @param options Optional configuration
   * @returns Unsubscribe function
   */
  on<T = EventPayload>(
    type: EventType,
    listener: EventListener<T>,
    options?: {
      once?: boolean;
      id?: string;
    }
  ): () => void {
    const subscription: EventSubscription = {
      id: options?.id || `sub-${++this.subscriptionCounter}`,
      type,
      listener: listener as EventListener,
      once: options?.once ?? false,
      subscribedAt: Date.now(),
    };

    // Get existing subscriptions for this event type
    const existing = this.subscriptions.get(type) || [];

    // Check for duplicate IDs
    if (options?.id && existing.some((sub) => sub.id === options.id)) {
      logger.warn(`Duplicate subscription ID: ${options.id}`, undefined, 'EventBus');
      // Return existing unsubscribe function
      return () => this.off(type, options.id!);
    }

    // Add subscription
    existing.push(subscription);
    this.subscriptions.set(type, existing);

    logger.debug(`Subscribed to ${type} (ID: ${subscription.id})`, undefined, 'EventBus');

    // Return unsubscribe function
    return () => this.off(type, subscription.id);
  }

  /**
   * Subscribe to an event (alias for `on`)
   */
  subscribe<T = EventPayload>(
    type: EventType,
    listener: EventListener<T>,
    options?: {
      once?: boolean;
      id?: string;
    }
  ): () => void {
    return this.on(type, listener, options);
  }

  /**
   * Subscribe to an event once (auto-unsubscribe after first execution)
   */
  once<T = EventPayload>(type: EventType, listener: EventListener<T>): () => void {
    return this.on(type, listener, { once: true });
  }

  /**
   * Unsubscribe from an event
   *
   * @param type Event type
   * @param id Subscription ID (optional, if not provided, removes all)
   */
  off(type: EventType, id?: string): void {
    const existing = this.subscriptions.get(type);
    if (!existing) {
      logger.warn(`No subscriptions found for ${type}`, undefined, 'EventBus');
      return;
    }

    if (id) {
      // Remove specific subscription
      const filtered = existing.filter((sub) => sub.id !== id);
      if (filtered.length === existing.length) {
        logger.warn(`Subscription not found: ${id}`, undefined, 'EventBus');
        return;
      }
      this.subscriptions.set(type, filtered);
      logger.debug(`Unsubscribed from ${type} (ID: ${id})`, undefined, 'EventBus');
    } else {
      // Remove all subscriptions for this event type
      this.subscriptions.delete(type);
      logger.debug(`Unsubscribed all listeners from ${type}`, undefined, 'EventBus');
    }
  }

  /**
   * Unsubscribe from an event (alias for `off`)
   */
  unsubscribe(type: EventType, id?: string): void {
    this.off(type, id);
  }

  /**
   * Emit an event (synchronous)
   *
   * @param type Event type
   * @param payload Event payload
   */
  emit<T = EventPayload>(type: EventType, payload?: T): void {
    const subscriptions = this.subscriptions.get(type);
    if (!subscriptions || subscriptions.length === 0) {
      logger.debug(`No listeners for ${type}`, undefined, 'EventBus');
      return;
    }

    logger.debug(`Emitting ${type} to ${subscriptions.length} listeners`, payload, 'EventBus');

    // Call each listener
    const toRemove: string[] = [];
    for (const subscription of subscriptions) {
      try {
        subscription.listener(payload as EventPayload);

        // Mark for removal if "once" flag is set
        if (subscription.once) {
          toRemove.push(subscription.id);
        }
      } catch (error) {
        logger.error(`Event listener error (${type}, ID: ${subscription.id})`, error, 'EventBus');
      }
    }

    // Remove "once" subscriptions
    if (toRemove.length > 0) {
      const remaining = subscriptions.filter((sub) => !toRemove.includes(sub.id));
      this.subscriptions.set(type, remaining);
    }
  }

  /**
   * Emit an event (alias for `emit`)
   */
  publish<T = EventPayload>(type: EventType, payload?: T): void {
    this.emit(type, payload);
  }

  /**
   * Emit an event asynchronously
   *
   * @param type Event type
   * @param payload Event payload
   */
  async emitAsync<T = EventPayload>(type: EventType, payload?: T): Promise<void> {
    const subscriptions = this.subscriptions.get(type);
    if (!subscriptions || subscriptions.length === 0) {
      logger.debug(`No listeners for ${type}`, undefined, 'EventBus');
      return;
    }

    logger.debug(
      `Emitting ${type} to ${subscriptions.length} listeners (async)`,
      payload,
      'EventBus'
    );

    // Call each listener (await if async)
    const toRemove: string[] = [];
    for (const subscription of subscriptions) {
      try {
        await subscription.listener(payload as EventPayload);

        // Mark for removal if "once" flag is set
        if (subscription.once) {
          toRemove.push(subscription.id);
        }
      } catch (error) {
        logger.error(`Event listener error (${type}, ID: ${subscription.id})`, error, 'EventBus');
      }
    }

    // Remove "once" subscriptions
    if (toRemove.length > 0) {
      const remaining = subscriptions.filter((sub) => !toRemove.includes(sub.id));
      this.subscriptions.set(type, remaining);
    }
  }

  /**
   * Clear all subscriptions (e.g., on logout)
   */
  clearAll(): void {
    const total = Array.from(this.subscriptions.values()).reduce(
      (sum, subs) => sum + subs.length,
      0
    );
    this.subscriptions.clear();
    logger.info(`Cleared ${total} event subscriptions`, undefined, 'EventBus');
  }

  /**
   * Get statistics
   */
  getStats() {
    const types = Array.from(this.subscriptions.entries()).map(([type, subs]) => ({
      type,
      count: subs.length,
      subscriptions: subs.map((sub) => ({
        id: sub.id,
        once: sub.once,
        subscribedAt: sub.subscribedAt,
        age: Date.now() - sub.subscribedAt,
      })),
    }));

    return {
      totalTypes: this.subscriptions.size,
      totalSubscriptions: types.reduce((sum, t) => sum + t.count, 0),
      types,
    };
  }

  /**
   * Log all subscriptions (for debugging)
   */
  logStats(): void {
    const stats = this.getStats();
    logger.info('Event Bus statistics:', stats, 'EventBus');
  }
}

// ============================================================================
// Singleton Instance
// ============================================================================

/**
 * Singleton instance of the Event Bus
 */
export const eventBus = new EventBus();

// ============================================================================
// Cleanup on window unload
// ============================================================================

// Automatically clear all subscriptions when the page unloads
window.addEventListener('beforeunload', () => {
  eventBus.clearAll();
});

// ============================================================================
// Example Usage (for documentation)
// ============================================================================

/*
Example usage:

// Service A (Inventory Service):
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';

async function updateInventory(ean: string, quantity: number) {
  // Update database...
  await supabase.rpc('adjust_inventory_delta', { ... });
  
  // Notify listeners
  eventBus.emit(EVENT_TYPES.INVENTORY_UPDATED, { ean, quantity });
}

// Service B (Cache Service):
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';

function initialize() {
  // Listen for inventory updates
  eventBus.on(EVENT_TYPES.INVENTORY_UPDATED, (payload) => {
    console.log('Inventory updated:', payload);
    invalidateCache(payload.ean);
  });
}

// Component (React):
import { eventBus, EVENT_TYPES } from '@/utils/eventBus';
import { useEffect } from 'react';

function InventoryList() {
  useEffect(() => {
    // Subscribe
    const unsubscribe = eventBus.on(EVENT_TYPES.INVENTORY_UPDATED, () => {
      // Refresh list
      refetch();
    });
    
    // Cleanup
    return unsubscribe;
  }, []);
  
  // ...
}
*/
